import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function (...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Format date to relative time (e.g., "2 days ago", "in 3 hours")
 */
export function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  
  // Time difference in milliseconds
  const diffMs = targetDate.getTime() - now.getTime();
  const diffAbsMs = Math.abs(diffMs);
  
  // Convert to seconds, minutes, hours, days
  const diffSeconds = Math.floor(diffAbsMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  
  // Format output based on time difference
  const isPast = diffMs < 0;
  
  if (diffSeconds < 60) {
    return isPast ? 'Just now' : 'In a few seconds';
  } else if (diffMinutes < 60) {
    return isPast 
      ? `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago` 
      : `In ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
  } else if (diffHours < 24) {
    return isPast 
      ? `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago` 
      : `In ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  } else if (diffDays < 7) {
    if (diffDays === 0) return isPast ? 'Today' : 'Today';
    if (diffDays === 1) return isPast ? 'Yesterday' : 'Tomorrow';
    return isPast 
      ? `${diffDays} days ago` 
      : `In ${diffDays} days`;
  } else if (diffWeeks < 4) {
    return isPast 
      ? `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago` 
      : `In ${diffWeeks} week${diffWeeks !== 1 ? 's' : ''}`;
  } else {
    return isPast 
      ? `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago` 
      : `In ${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
  }
}

/**
 * Format due date for projects (e.g., "Due today", "Due in 2 weeks")
 */
export function formatDueDate(date: Date | string): string {
  const now = new Date();
  const dueDate = typeof date === 'string' ? new Date(date) : date;
  
  // Time difference in milliseconds
  const diffMs = dueDate.getTime() - now.getTime();
  
  // Convert to days
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    const absDays = Math.abs(diffDays);
    return absDays === 1 ? 'Due yesterday' : `Overdue by ${absDays} days`;
  } else if (diffDays === 0) {
    return 'Due today';
  } else if (diffDays === 1) {
    return 'Due tomorrow';
  } else if (diffDays <= 7) {
    return `Due in ${diffDays} days`;
  } else if (diffDays <= 14) {
    return 'Due in 2 weeks';
  } else if (diffDays <= 30) {
    return 'Due in a month';
  } else {
    return `Due on ${dueDate.toLocaleDateString()}`;
  }
}
